import { AxiosProgressEvent } from "axios";
import { useTranslation } from "react-i18next";
import AuthService from "src/services/authService";
import ElasticService from "src/services/elasticService";
import { useSelector } from "src/store";
import { selectCurrentWorkspaceId } from "src/store/app";

import { useUploadFileMutation } from "@dashboard/files/api";
import { useUploadContext } from "@dashboard/files/context/UploadContext";
import { showSuccessToast, showWarningToast } from "@shared/toasts/Toasts";

// 256 MB in bytes
export const maxFileSize = 256 * 1024 * 1024;

const forbiddenFileTypes = [
    "apk+",
    "apkm",
    "apkx",
    "deb",
    "exe",
    "gif",
    "htm",
    "html",
    "jpeg",
    "jpg",
    "mp3",
    "png",
    "ppt",
    "pptx",
];

export const useFileUpload = ({ groupId }: { groupId?: number } = {}) => {
    const { t } = useTranslation(["common"]);
    const { abortController, setUploadStarted, setUploadProgress, setUploadedFileIds, setSelectedFiles } = useUploadContext();
    const [uploadFile] = useUploadFileMutation();
    const workspaceId = useSelector(selectCurrentWorkspaceId);
    const user = AuthService.getCurrentUser();

    const validateFiles = (files: File[]) => {
        for (const file of files) {
            const fileExtension = file.name.split(".").pop()?.toLowerCase();
            if (forbiddenFileTypes.includes(fileExtension || "")) {
                showWarningToast(t("common:section.uploadField.unsupportedFileType"));
                return false;
            }
        }

        for (const file of files) {
            if (file.size > maxFileSize) {
                showWarningToast(t("common:section.uploadField.maxSizeExceeded"));
                return false;
            }
        }

        return true;
    };

    const uploadFiles = (files: File[]) => {
        if (!files.length || !workspaceId) {
            return;
        }

        setUploadStarted(true);
        let completedFiles = 0;
        const progressPerFile = files.length === 1 ? 0 : 100 / files.length;

        Promise.all(
            files.map((file) => {
                if (abortController.signal.aborted) {
                    throw new Error("Upload canceled");
                }

                return uploadFile({
                    workspaceId,
                    filename: file.name,
                    fileToUpload: file,
                    controller: abortController,
                    groupId: groupId || null,
                    onUploadProgress: (progressEvent: AxiosProgressEvent) => {
                        if (files.length === 1 && progressEvent.total) {
                            const fileProgress = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                            setUploadProgress(fileProgress);
                        } else {
                            const currentProgress = Math.round(completedFiles * progressPerFile);
                            setUploadProgress(currentProgress);
                        }
                    },
                    setUploadedFileIds,
                })
                    .unwrap()
                    .then(() => {
                        completedFiles++;
                        if (files.length > 1) {
                            const newProgress = Math.round(completedFiles * progressPerFile);
                            setUploadProgress(newProgress);
                        }
                        if (!abortController.signal.aborted && user?.email) {
                            showSuccessToast(t("common:section.uploadField.toast.fileUploadedSuccess"));
                            ElasticService.createTransaction("upload", {
                                user_email: user.email,
                                file_name: file.name,
                                file_size: file.size,
                            });
                        }
                    });
            }),
        )
            .catch(() => {
                // Error toast is shown in files API upload mutation (rtkClient).
            })
            .finally(() => {
                setUploadProgress(0);
                setUploadStarted(false);
                setSelectedFiles([]);
            });
    };

    const onFilesSelected = (files: File[]) => {
        if (!files?.length) {
            return;
        }

        if (validateFiles(files)) {
            setSelectedFiles(files);
            uploadFiles(files);
        }
    };

    return { onFilesSelected };
};
